import {
  Button,
  Checkbox,
  TextAreaField,
  useToast,
  Avatar,
  AvatarFallback,
  Icon,
} from "@elaniintc/goes-design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useApproveCancelFormalityRequest } from "@/queries/formality-request";
import {
  cancelVeredictSchema,
  type CancelVeredictFormData,
} from "@/schemas/formality-request/cancel-veredict";
import SelectField from "@/components/common/select";
import { cn } from "@/utils/theme";
import ScrollModal from "@/components/common/scroll-modal";

interface VeredictCancelRequestModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  requestCode: string;
  cancellationId: string;
  cancellationReason?: string;
}

export const VeredictCancelRequestModal = ({
  isOpen = false,
  onClose = () => {},
  requestCode,
  cancellationId,
  cancellationReason,
}: VeredictCancelRequestModalProps) => {
  const { showToast } = useToast();
  const { mutateAsync: cancelRequest, isPending } =
    useApproveCancelFormalityRequest();

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
  } = useForm<CancelVeredictFormData>({
    mode: "onChange",
    resolver: zodResolver(cancelVeredictSchema),
    defaultValues: {
      veredict: "" as "APPROVE" | "REJECT",
      cancellationReason: cancellationReason || "",
      confirmationChecked: false,
    },
  });

  const veredictoValue = watch("veredict");

  const getVeredictLabel = () => {
    if (veredictoValue === "REJECT") return "No desistir del trámite";
    if (veredictoValue === "APPROVE") return "Desistir del trámite";
    return "Sin veredicto";
  };

  const getVeredictStyles = () => {
    const isReject = veredictoValue === "REJECT";
    const isApprove = veredictoValue === "APPROVE";

    const getCardBackground = () => {
      if (isReject) return "bg-neutral-object-accent-quiet text-on-disabled";
      if (isApprove)
        return "bg-surface-danger-accent-quiet contextual-text-danger";
      return "bg-surface-warning-accent-quiet contextual-text-warning";
    };

    const getAvatarColor = () => {
      if (isReject) return "neutral";
      if (isApprove) return "danger";
      return "warning";
    };

    const getAvatarTextColor = () => {
      if (isReject) return "text-body-quiet";
      if (isApprove) return "text-danger-quiet";
      return "text-warning-quiet";
    };

    return {
      cardBackground: getCardBackground(),
      avatarColor: getAvatarColor(),
      avatarTextColor: getAvatarTextColor(),
      checkboxColor: isApprove ? "checked:bg-surface-danger-solid" : "",
    } as const;
  };

  const veredictStyles = getVeredictStyles();

  const onSubmit = async (data: CancelVeredictFormData) => {
    try {
      await cancelRequest({
        approved: data.veredict === "APPROVE",
        cancellation_request_id: cancellationId,
      });

      showToast({
        title: "Veredicto enviado",
        description: "El veredicto ha sido enviado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });

      reset();
      onClose();
    } catch (error: unknown) {
      console.error("Error sending veredict:", error);
      showToast({
        title: "Error al enviar veredicto",
        description:
          "No se pudo enviar el veredicto. Por favor, intenta nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const handleBack = () => {
    reset();
    onClose();
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      modalFooterClassName="px-8 py-4 bg-neutral-foreground border-t-0"
      modalHeaderClassName="p-8 py-4 bg-neutral-foreground border-b-0"
      modalChildrenClassName="px-8 bg-neutral-foreground"
      modalContentClassName="max-w-[600px] bg-neutral-foreground h-95vh max-h-[98vh]"
      header={
        <div className="text-center">
          <h1 className="text-center text-h2! font-semibold text-heading leading-[1.3] tracking-[-0.02em]">
            Solicitud de desistimiento
          </h1>
          <p className="text-body text-base leading-relaxed">
            Aprueba o deniega la solicitud para desistir del trámite{" "}
            <span className="font-semibold">{requestCode}</span>, se detendrá
            por completo y todo el proceso que se ha realizado hasta ahora se
            perderá.
          </p>
        </div>
      }
      footer={
        <div className="flex flex-1 gap-3">
          <Button
            type="button"
            variant="tertiary"
            className="flex-1"
            onClick={handleBack}
            disabled={isPending}
          >
            Volver
          </Button>
          <Button
            type="button"
            variant={veredictoValue === "REJECT" ? "default" : "danger-primary"}
            className="flex-1"
            onClick={handleFormSubmit(onSubmit)}
            disabled={!isValid || isPending}
          >
            {isPending ? "Enviando..." : "Enviar veredicto"}
            {isPending && (
              <Icon
                type="fas"
                name="spinner-third"
                size="sm"
                className="animate-spin"
              />
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Warning List Section */}
        <div className="space-y-2">
          <div
            className={cn(
              "w-full p-2 rounded-lg flex items-center gap-3",
              veredictStyles.cardBackground,
            )}
          >
            <Avatar size="xs">
              <AvatarFallback
                color={veredictStyles.avatarColor}
                className={veredictStyles.avatarTextColor}
              >
                <Icon type="fas" name="dollar" className="text-xs" />
              </AvatarFallback>
            </Avatar>
            <p className="text-contextual-danger text-sm leading-7">
              Los pagos realizados no aplican para nuevas solicitudes
            </p>
          </div>

          <div
            className={cn(
              "w-full p-2 rounded-lg flex items-center gap-3",
              veredictStyles.cardBackground,
            )}
          >
            <Avatar size="xs">
              <AvatarFallback
                color={veredictStyles.avatarColor}
                className={veredictStyles.avatarTextColor}
              >
                <Icon type="fas" name="list" className="text-xs" />
              </AvatarFallback>
            </Avatar>
            <p className="text-contextual-danger text-sm leading-7">
              El progreso de los formularios y sus correcciones se perderán
            </p>
          </div>

          <div
            className={cn(
              "w-full p-2 rounded-lg flex items-center gap-3",
              veredictStyles.cardBackground,
            )}
          >
            <Avatar size="xs">
              <AvatarFallback
                color={veredictStyles.avatarColor}
                className={veredictStyles.avatarTextColor}
              >
                <Icon type="fas" name="edit" className="text-xs" />
              </AvatarFallback>
            </Avatar>
            <p className="text-contextual-danger text-sm leading-7">
              Si se han realizado cambios a los expedientes, estos no serán
              revertidos
            </p>
          </div>
        </div>

        {/* Form Section */}
        <div className="space-y-2">
          <SelectField
            label="Veredicto"
            required
            options={[
              { label: "No desistir del trámite", value: "REJECT" },
              { label: "Desistir del trámite", value: "APPROVE" },
            ]}
            {...register("veredict")}
            placeholder="Seleccionar"
            error={errors.veredict?.message}
            name="veredicto"
            onChange={(value) => {
              if (value === "APPROVE" || value === "REJECT") {
                setValue("veredict", value, {
                  shouldValidate: true,
                });
              }
            }}
            value={veredictoValue}
          />

          <TextAreaField
            label="Motivo de desistimiento"
            required
            readOnly
            {...register("cancellationReason")}
            placeholder="Escribe el motivo por el cual estás desistiendo de la solicitud"
            className="w-full resize-none read-only:bg-neutral-object-accent-silent read-only:text-on-disabled"
            error={errors.cancellationReason?.message}
          />

          {/* Confirmation Section */}
          <div className="flex items-start gap-2.5">
            <div>
              <Checkbox
                id="confirmation"
                {...register("confirmationChecked")}
                className={veredictStyles.checkboxColor}
              />
            </div>
            <label
              htmlFor="confirmation"
              className="text-body text-sm leading-7 flex-1"
            >
              He leído detenidamente las implicaciones, y confirmo la elección
              del veredicto "{getVeredictLabel()}" para el desistimiento de este
              trámite
            </label>
          </div>
          {errors.confirmationChecked && (
            <div className="text-danger mt-1 flex items-center gap-1">
              <Icon type="far" name="circle-xmark" size="xs" />
              <p className="text-xs ">{errors.confirmationChecked.message}</p>
            </div>
          )}
        </div>
      </div>
    </ScrollModal>
  );
};
